import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc,
  setDoc,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { USER_ROLES, ROLE_PERMISSIONS, AUDIT_ACTIONS, RESOURCE_TYPES } from '../types/rbac';

/**
 * Authorization Service
 * Handles role-based access control and tenant scoping
 */

// Global state for current user and scoped session
let currentUser = null;
let scopedAccountId = null;
let scopedAccountName = null;

// Event listeners for account scope changes
let scopeChangeListeners = [];

/**
 * Initialize authorization with current user
 * @param {Object} user - Firebase user object
 * @param {string} user.uid - User ID
 * @param {string} user.email - User email
 */
export const initializeAuth = async (user) => {
  if (!user) {
    currentUser = null;
    scopedAccountId = null;
    scopedAccountName = null;
    return;
  }

  try {
    console.log('🔍 Looking for user document with UID:', user.uid);
    // Get user profile from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      console.log('📄 Found user document:', userData);
      currentUser = {
        uid: user.uid,
        email: user.email,
        role: userData.role || USER_ROLES.CLIENT,
        accountId: userData.accountId || null,
        accountName: userData.accountName || null
      };
      console.log('✅ User initialized with role:', currentUser.role);
    } else {
      console.log('❌ User document not found, creating new profile');
      // Create user profile if doesn't exist
      currentUser = {
        uid: user.uid,
        email: user.email,
        role: USER_ROLES.CLIENT,
        accountId: null,
        accountName: null
      };
      await createUserProfile(user.uid, user.email);
    }
  } catch (error) {
    console.error('Error initializing auth:', error);
    throw error;
  }
};

/**
 * Create user profile in Firestore
 * @param {string} uid - User ID
 * @param {string} email - User email
 */
const createUserProfile = async (uid, email) => {
  try {
    // Check if user should be an admin based on email
    const adminEmails = [
      'eric@lithoco.com',
      'eric@shift.healthcare',
      'admin@shift.healthcare'
    ];
    
    const userRole = adminEmails.includes(email.toLowerCase()) ? USER_ROLES.ADMIN : USER_ROLES.CLIENT;
    
    console.log(`🔐 Creating user profile for ${email} with role: ${userRole}`);
    
    await setDoc(doc(db, 'users', uid), {
      uid,
      name: email, // Use email as default name
      email,
      role: userRole,
      status: 'active',
      accountId: null,
      accountName: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Update currentUser with the correct role
    if (currentUser && currentUser.uid === uid) {
      currentUser.role = userRole;
      console.log(`✅ Updated currentUser role to: ${userRole}`);
    }
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

/**
 * Get the effective account ID for the current user
 * For CLIENT users: returns their own accountId
 * For ADMIN users: returns the currently scoped accountId
 * @returns {string|null} Effective account ID
 */
export const getEffectiveAccountId = () => {
  if (!currentUser) return null;
  
  if (currentUser.role === USER_ROLES.CLIENT) {
    return currentUser.accountId;
  }
  
  if (currentUser.role === USER_ROLES.ADMIN) {
    return scopedAccountId;
  }
  
  return null;
};

/**
 * Get the effective account name for the current user
 * @returns {string|null} Effective account name
 */
export const getEffectiveAccountName = () => {
  if (!currentUser) return null;
  
  if (currentUser.role === USER_ROLES.CLIENT) {
    return currentUser.accountName;
  }
  
  if (currentUser.role === USER_ROLES.ADMIN) {
    return scopedAccountName;
  }
  
  return null;
};

/**
 * Check if user has a specific permission
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has permission
 */
export const hasPermission = (permission) => {
  if (!currentUser) return false;
  
  const rolePermissions = ROLE_PERMISSIONS[currentUser.role] || [];
  return rolePermissions.includes(permission);
};

/**
 * Check if user is admin
 * @returns {boolean} True if user is admin
 */
export const isAdmin = () => {
  return currentUser?.role === USER_ROLES.ADMIN;
};

/**
 * Check if user is client
 * @returns {boolean} True if user is client
 */
export const isClient = () => {
  return currentUser?.role === USER_ROLES.CLIENT;
};

/**
 * Check if admin is currently scoped to an account
 * @returns {boolean} True if admin is scoped
 */
export const isScoped = () => {
  return isAdmin() && scopedAccountId !== null;
};

/**
 * Get current user info
 * @returns {Object|null} Current user object
 */
export const getCurrentUser = () => {
  return currentUser;
};

/**
 * Set scoped account for admin
 * @param {string} accountId - Account ID to scope to
 * @param {string} accountName - Account name
 */
export const setScopedAccount = async (accountId, accountName) => {
  if (!isAdmin()) {
    throw new Error('Only admins can set scoped account');
  }
  
  const previousAccountId = scopedAccountId;
  scopedAccountId = accountId;
  scopedAccountName = accountName;
  
  // Log scope change
  await logActivity(AUDIT_ACTIONS.ENTER_ACCOUNT_SCOPE, RESOURCE_TYPES.ACCOUNT, accountId, {
    previousAccountId,
    accountName
  });
  
  // Persist in localStorage for client-side
  localStorage.setItem('scopedAccountId', accountId);
  localStorage.setItem('scopedAccountName', accountName);
  
  // Notify listeners of scope change
  notifyScopeChange();
};

/**
 * Clear scoped account for admin
 */
export const clearScopedAccount = async () => {
  if (!isAdmin()) {
    throw new Error('Only admins can clear scoped account');
  }
  
  const previousAccountId = scopedAccountId;
  const previousAccountName = scopedAccountName;
  
  scopedAccountId = null;
  scopedAccountName = null;
  
  // Log scope change
  await logActivity(AUDIT_ACTIONS.EXIT_ACCOUNT_SCOPE, RESOURCE_TYPES.ACCOUNT, previousAccountId, {
    previousAccountName
  });
  
  // Clear from localStorage
  localStorage.removeItem('scopedAccountId');
  localStorage.removeItem('scopedAccountName');
  
  // Notify listeners of scope change
  notifyScopeChange();
};

/**
 * Restore scoped account from localStorage
 */
export const restoreScopedAccount = () => {
  if (!isAdmin()) return;
  
  const storedAccountId = localStorage.getItem('scopedAccountId');
  const storedAccountName = localStorage.getItem('scopedAccountName');
  
  if (storedAccountId && storedAccountName) {
    scopedAccountId = storedAccountId;
    scopedAccountName = storedAccountName;
  }
};

/**
 * Get all accounts that admin can access
 * @returns {Promise<Array>} Array of accounts
 */
export const getAccessibleAccounts = async () => {
  if (!isAdmin()) {
    throw new Error('Only admins can access account list');
  }
  
  try {
    const accountsRef = collection(db, 'accounts');
    const querySnapshot = await getDocs(accountsRef);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting accessible accounts:', error);
    throw error;
  }
};

/**
 * Validate that a resource belongs to the effective account
 * @param {Object} resource - Resource object
 * @param {string} resource.accountId - Resource's account ID
 * @returns {boolean} True if resource belongs to effective account
 */
export const validateResourceAccess = (resource) => {
  const effectiveAccountId = getEffectiveAccountId();
  
  if (!effectiveAccountId) {
    return false;
  }
  
  return resource.accountId === effectiveAccountId;
};

/**
 * Log activity for auditing
 * @param {string} action - Action performed
 * @param {string} resourceType - Type of resource
 * @param {string} resourceId - ID of resource
 * @param {Object} metadata - Additional metadata
 */
export const logActivity = async (action, resourceType, resourceId, metadata = {}) => {
  if (!currentUser) return;
  
  try {
    const activityLog = {
      timestamp: serverTimestamp(),
      actorUserId: currentUser.uid,
      actorRole: currentUser.role,
      actorEmail: currentUser.email,
      effectiveAccountId: getEffectiveAccountId(),
      effectiveAccountName: getEffectiveAccountName(),
      action,
      resourceType,
      resourceId,
      metadata,
      createdAt: serverTimestamp()
    };
    
    await addDoc(collection(db, 'activity_logs'), activityLog);
  } catch (error) {
    console.error('Error logging activity:', error);
    // Don't throw error for logging failures
  }
};

/**
 * Create account for client
 * @param {string} name - Account name
 * @param {string} clientUserId - Client user ID
 * @returns {Promise<string>} Account ID
 */
export const createAccount = async (name, clientUserId) => {
  if (!isAdmin()) {
    throw new Error('Only admins can create accounts');
  }
  
  try {
    const accountData = {
      name,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: currentUser.uid
    };
    
    const docRef = await addDoc(collection(db, 'accounts'), accountData);
    
    // Update client user with account ID
    await updateUserAccount(clientUserId, docRef.id, name);
    
    await logActivity(AUDIT_ACTIONS.ACCOUNT_CREATED, RESOURCE_TYPES.ACCOUNT, docRef.id, {
      accountName: name,
      clientUserId
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating account:', error);
    throw error;
  }
};

/**
 * Update user with account information
 * @param {string} userId - User ID
 * @param {string} accountId - Account ID
 * @param {string} accountName - Account name
 */
const updateUserAccount = async (userId, accountId, accountName) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      accountId,
      accountName,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user account:', error);
    throw error;
  }
};

/**
 * Add listener for account scope changes
 * @param {Function} listener - Function to call when scope changes
 * @returns {Function} Unsubscribe function
 */
export const addScopeChangeListener = (listener) => {
  scopeChangeListeners.push(listener);
  
  // Return unsubscribe function
  return () => {
    const index = scopeChangeListeners.indexOf(listener);
    if (index > -1) {
      scopeChangeListeners.splice(index, 1);
    }
  };
};

/**
 * Notify all listeners of scope change
 */
const notifyScopeChange = () => {
  scopeChangeListeners.forEach(listener => {
    try {
      listener({
        scopedAccountId,
        scopedAccountName,
        effectiveAccountId: getEffectiveAccountId(),
        effectiveAccountName: getEffectiveAccountName()
      });
    } catch (error) {
      console.error('Error in scope change listener:', error);
    }
  });
};
